import { Updater } from "@tanstack/react-table";
import { ContentController, ContentPostContentLocationDownloadDto, ControllerHelper, ControllerMethod, FetchPostsFilter, FileEntryDownloadDto, FileEntrySearchFilter, PostDownloadDto, ProjectGroupDownloadDto, TokenContainer } from "collaboration-service";
import { SearchResultsDto } from "collaboration-service/dist/dtos/SearchResultsDto";
import AutocompleteSearch from "components/AutocompleteSearch/AutocompleteSearch";
import { GeneralSC as G } from "components/General/GeneralSC";
import LoadingDataTable, { ColumnDefintion, LoadingDataTableProps, PagingHandler } from "components/LoadingDataTable/LoadingDataTable";
import { ButtonProps, Icon, Input } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from "react";
import Autosuggest, { InputProps } from "react-autosuggest";
import { useSelector } from "react-redux";
import { Actions } from "services/ApplicationState/Actions";
import { useAppDispatch } from "services/ApplicationState/ApplicationState";
import { shallowCompare } from "services/Helpers";
import { getCMSContent } from "services/Helpers/TranslationHelpers";
import { useImgI18N } from "services/ImgI18N";
import { UseCMSSearchNeededActions, UseCMSSearchStateMapper, UseDataFromRouteState, updateCMSSearchOnRoute, useCMSSearchDataFromRoute } from "./useCMSSearchHelpers";
import { useUseCMSSearchDispatch } from "./useCMSSearchStateHandling";

const empty = {};


type ResultColums = "created" | "changed" | "version" | "language" | "preview" | "publication" | "author" | "duration";

export interface useCMSSearchSelectButtonProps<T> extends Omit<ButtonProps, "floated" | "onClick"> {
    onClick?: (item: T) => void;
}

export type MaxCountHandling = {
    setMaxCount: (count: number) => void;
    getMaxCount: () => number;
};

interface BaseSearchData {
    token?: TokenContainer;
    maxCount?: number | null;
    filter?: FetchPostsFilter;
    feFilter?: FileEntrySearchFilter;
    text: string;
    lng: string;
}

export interface useCMSSearchProps<T extends { id: string }, SearchData extends BaseSearchData> {
    debounceSearch?: number;
    namespace?: string;
    onFileClicked?: (projectId: string, fileId: string) => void;
    onProjectClicked?: (projectId: string) => void;
    hidden?: Array<ResultColums>;
    onRefineLoadedData?: (data: SearchResultsDto<T>) => Promise<SearchResultsDto<T>>;
    stateMapper: UseCMSSearchStateMapper<T>;
    searchMethod: ControllerMethod<SearchResultsDto<T>, SearchData>;
    getSuggestionsMethod: ControllerMethod<string[], SearchData>;
    actions: UseCMSSearchNeededActions<T>;
    searchData: Omit<SearchData, "filter" | "text" | "lng" | "feFilter">;
    columns?: ColumnDefintion<T>[];
    renderSubComponent?: LoadingDataTableProps<T, any, any>["renderSubComponent"];
    multiSelect?: boolean;
    setLoadingPage?: (val: boolean) => void;
    maxCountHandling?: MaxCountHandling;
    onShowPreview?: (toShow: T) => void;
    placeholderText?: string;
    dontUseRouteForState?: boolean;
    changeSelectionHandler?: LoadingDataTableProps<T, any, any>["changeSelectionHandler"];
    renderSearchResults?: (p: useCMSSearchBasicSearchResultsProps<T, SearchData>) => JSX.Element;
    dontResetSearchResults?: boolean;
    searchResultTableWidthScaling?: number;
    onChangeTerm?: (s: string) => void;
}


interface useCMSSearchSearchResultsProps<T extends { id: string }, SearchData extends BaseSearchData> {
    getFilename: (file: FileEntryDownloadDto, project: ProjectGroupDownloadDto) => string;
    searchResults: SearchResultsDto<T> | undefined;
    t: (val: string, data?: { [key: string]: any; } | undefined) => string;
    filePosts: { [key: string]: PostDownloadDto[]; };
    lng: string;
    location?: ContentPostContentLocationDownloadDto;
    locations?: ContentPostContentLocationDownloadDto[];
    hidden: useCMSSearchProps<T, SearchData>["hidden"];
    columns: useCMSSearchProps<T, SearchData>["columns"];
    multiSelect?: boolean;
    changeSelectionHandler?: useCMSSearchProps<T, SearchData>["changeSelectionHandler"];
}
interface SearchFieldProps {
    term?: string;
    placeholderText?: string;
    getSuggestions: (text: string) => Promise<string[]>;
    clearInput: () => void;
    updateState: typeof updateCMSSearchOnRoute;
    ordered?: boolean;
}
const SearchField = (p: SearchFieldProps) => {
    const [term, setTerm] = React.useState<string>();
    const [suggestions, setSuggestions] = React.useState<string[]>([]);
    const { term: outerTerm, getSuggestions, placeholderText, /*clearInput,*/ updateState, ordered } = p;
    const clearSuggestions = React.useMemo(() => () => setSuggestions([]), [setSuggestions]);
    React.useEffect(() => setTerm(outerTerm), [outerTerm]);
    const fetchSuggestions = React.useMemo(() => (request: Autosuggest.SuggestionsFetchRequestedParams) => {
        //console.log("useCMSSearch.tsx::101 => request", request);

        const l = async () => {
            const term = request.value;
            if (term) {
                const res = await getSuggestions(term);
                setSuggestions(res ?? []);
            }
            else
                setSuggestions([]);
        };
        l();
    }, [getSuggestions]);

    const onEnter = React.useMemo(() => () => setTerm(t => {
        updateState(t, undefined, undefined, undefined, undefined, undefined, false);
        return t;
    }), [setTerm, updateState]);

    const renderInputComponent = React.useMemo(() => (inputProps: InputProps<string>) => {
        const { onChange, onBlur, width, size, ref, ...rest } = inputProps as any;
        const imgOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChange(event, { newValue: event.target.value, method: "type" })
        }
        return (
            <>
                <div style={{ position: "relative" }}>
                    <Input
                        width="100%"
                        onChange={imgOnChange}
                        type="search"
                        kind="borderless"
                        inline
                        noMargin
                        height="40px"
                        innerRef={ref}
                        {...(rest as any)}
                    />
                    {/* {term &&
                        <G.ResetSearchIcon onClick={clearInput} style={{ right: 80 }}>
                            <Icon name="times" color={"@middleLightGrey"} />
                        </G.ResetSearchIcon>
                    } */}
                    <G.ResetSearchIconSearch>
                        <Icon name="search" />
                    </G.ResetSearchIconSearch>
                </div>

            </>
        );

    }, [/*term, clearInput*/]);

    return (
        <AutocompleteSearch
            onSuggestionSelected={onEnter}
            onSuggestionsClearRequested={clearSuggestions}
            onSuggestionsFetchRequested={fetchSuggestions}
            placeholder={placeholderText ?? "search"}
            search={ordered && term === "*" ? "" : (term ?? "")}
            searchChanged={(e, p) => setTerm(p.newValue)}
            onEnterPressed={onEnter}
            suggestions={suggestions}
            renderInputComponent={renderInputComponent}
        />
    )
};



export type useCMSSearchBasicSearchResultsProps<T extends { id: string }, SearchData extends BaseSearchData> = useCMSSearchSearchResultsProps<T, SearchData> &
{
    isDisabled?: boolean,
    renderSubComponent?: LoadingDataTableProps<T, any, any>["renderSubComponent"],
    paging: PagingHandler;
    onSelectionChanged?: (selected: T[]) => void;
    initPageSize?: number;
    searchResultTableWidthScaling?: number;
};
const BasicSearchResults = <T extends { id: string }, SearchData extends BaseSearchData>(p: useCMSSearchBasicSearchResultsProps<T, SearchData>) => {
    const { searchResults, columns, renderSubComponent, multiSelect, paging, onSelectionChanged, isDisabled, changeSelectionHandler, searchResultTableWidthScaling } = p;
    const cols = React.useMemo(() => {
        const toRet: ColumnDefintion<T>[] = columns ?? [
            { elementName: "id", width: 1 }
        ]
        return toRet;
    }, [columns]);

    if (searchResults === undefined)
        return <div />;



    return <div>

        <LoadingDataTable
            columns={cols}
            loadData={p.searchResults?.results ?? [] as any}
            loaderData={empty}
            renderSubComponent={renderSubComponent}
            // initPageSize={initPageSize ?? cmsConfig.maxSearchCount}
            initPageSize={12}
            multiSelect={multiSelect}
            ownPaging={paging}
            noFilter
            noSorting
            disabled={isDisabled}
            onSelectionChanged={onSelectionChanged}
            changeSelectionHandler={changeSelectionHandler}
            widthScaling={searchResultTableWidthScaling}
        />
    </div>
};

type InternalActions =
    | { type: "setState", value: { text: string | undefined, filter: Updater<FetchPostsFilter> | undefined, feFilter: Updater<FileEntrySearchFilter> | undefined, lng: string | undefined, token: string | undefined, type: "projects" | "files" | undefined, ordered: boolean | undefined, r?: boolean } }
    ;

const reducer = (state: UseDataFromRouteState, action: InternalActions): UseDataFromRouteState => {
    switch (action.type) {
        case "setState":
            {
                const toRet = { ...state };
                let set = false;
                const { feFilter, filter, lng, text, token, type, r } = action.value;
                if (text !== undefined && toRet.text !== text) {
                    toRet.text = text;
                    set = true;
                }
                if (lng !== toRet.lng) {
                    toRet.lng = lng;
                    set = true;
                }
                if (token && token !== toRet.token) {
                    toRet.token = token;
                    set = true;
                }
                if (type && toRet.type !== type) {
                    toRet.type = type;
                    set = true;
                }
                if (r !== toRet.r) {
                    toRet.r = r;
                    set = true;
                }
                if (filter) {
                    const val = typeof filter === "function" ? filter(toRet.filter) : filter;
                    if (!_.isEqual(val, toRet.filter)) {
                        toRet.filter = val ?? { allowFuzzy: true };
                        set = true;
                    }

                }
                if (feFilter) {
                    const val = typeof feFilter === "function" ? feFilter(toRet.feFilter) : feFilter;
                    if (!_.isEqual(val, toRet.feFilter)) {
                        toRet.feFilter = val ?? {};
                        set = true;
                    }
                }

                return set ? toRet : state;
            }
    }
    return state;
}



const useCMSSearch = <T extends { id: string }, SearchData extends BaseSearchData>(p: useCMSSearchProps<T, SearchData>) => {
    const { namespace, onFileClicked, onProjectClicked, hidden, stateMapper,
        searchMethod, getSuggestionsMethod, actions, placeholderText,
        columns, renderSubComponent, dontUseRouteForState, changeSelectionHandler,
        onRefineLoadedData, multiSelect, setLoadingPage, maxCountHandling, onShowPreview,
        renderSearchResults, dontResetSearchResults, searchResultTableWidthScaling,
        debounceSearch, onChangeTerm } = p;
    const { t, currentLanguage } = useImgI18N(namespace ?? "cms");
    const { contentLanguage, location, searchResults, filePosts, locations } = useSelector(stateMapper, shallowCompare);
    const dispatch = useAppDispatch();
    const [loading, setLoading] = React.useState(false);
    const cMSSearchDispatch = useUseCMSSearchDispatch();
    const [selection, setSelection] = React.useState<T[]>([]);

    const [routeDataFromState, dp] = React.useReducer(reducer, { filter: { allowFuzzy: true }, feFilter: {} });
    const routeDataFromRoute = useCMSSearchDataFromRoute();
    const updateState = React.useMemo(() => (text: string | undefined, filter: Updater<FetchPostsFilter> | undefined, feFilter: Updater<FileEntrySearchFilter> | undefined, lng: string | undefined, token: string | undefined, type: "projects" | "files" | undefined, ordered: boolean | undefined, r?: boolean) => {
        //console.log("useCMSSearch.tsx::267 => dontUseRouteForState", dontUseRouteForState);

        if (dontUseRouteForState)
            dp({ type: "setState", value: { text, filter, feFilter, lng, token, type, ordered, r } });
        else {
            if (onChangeTerm)
                onChangeTerm(text ?? '*');
            updateCMSSearchOnRoute(text, filter, feFilter, lng, token, type, ordered, r);
        }
    }, [dontUseRouteForState, dp, onChangeTerm]);

    const routeData = dontUseRouteForState ? routeDataFromState : routeDataFromRoute;

    React.useEffect(() => {
        const l = async () => {
            const locs = await ControllerHelper.singleCall({ grouptype: 'CMS' }, ContentController.GetContentPostContentLocationForAllGroupsByGroupType, true)
            //            console.log("useCMSSearchNew.tsx::449 => locs", locs);

            dispatch(Actions.setCMSLocations(locs));
        };
        if (!locations || locations.length === 0)
            l();
    }, [locations, dispatch])

    const data = p.searchData;

    //const  {feFilter, filter} = routeData;
    const search = React.useMemo(() => {
        const call = async (routeData: UseDataFromRouteState, pageSize?: number, silent?: boolean) => {
            if (routeData.text === undefined || routeData.text === "") {
                if (!dontResetSearchResults) {
                    //console.log("useCMSSearch.tsx::310 => from here!");
                    dispatch(p.actions?.setSearchResults(undefined));
                }

                return;
            }
            if (!silent)
                setLoading(true);
            // const maxCount = pageSize ?? data.maxCount ?? cmsConfig.maxSearchCount;
            const maxCount = pageSize ?? data.maxCount ?? 12;
            dispatch(Actions.clearCMSSearchResultEntries());
            //dispatch(p.actions?.setSearchResults({ ...searchResults, results: [] }));
            const res = await ControllerHelper.singleCall({ ...data, ...routeData, token: { token: routeData.token }, maxCount } as any, searchMethod);
            if (onRefineLoadedData) {
                const runIt = async () => {
                    dispatch(p.actions?.setSearchResults(await onRefineLoadedData(res)));
                }
                runIt();
            }
            else
                dispatch(p.actions?.setSearchResults(res));
            if (!silent)
                setLoading(false);
        }
        return debounceSearch ? _.debounce(call, debounceSearch) : call;
    }, [data, dispatch, onRefineLoadedData, p.actions, searchMethod, dontResetSearchResults, debounceSearch]);

    React.useEffect(() => {
        //console.log("useCMSSearch.tsx::311 => routeData", routeData);
        search(routeData);
    }, [routeData, search])

    //React.useEffect(() => { dp({ type: "setFilter", value: gSFilter }); }, [dp, gSFilter]);
    //React.useEffect(() => { dp({ type: "setFeFilter", value: gSFeFilter }); }, [dp, gSFeFilter]);

    // console.log("useCMSSearchNew.tsx::650 => search", search);
    // React.useEffect(() => console.log("useCMSSearch.tsx::168 **Changed** => gSFilter", gSFilter), [gSFilter]);
    // React.useEffect(() => console.log("useCMSSearch.tsx::170 **Changed** => gSFeFilter", gSFeFilter), [gSFeFilter]);
    // React.useEffect(() => console.log("useCMSSearch.tsx::155 **Changed** => filter", filter), [filter]);
    // React.useEffect(() => console.log("useCMSSearch.tsx::156 **Changed** => fileEntryfilter", feFilter), [feFilter]);
    React.useEffect(() => {
        cMSSearchDispatch({ type: "setOnClickHandlers", value: { onFileClicked, onProjectClicked, onShowPreview } });
    }, [cMSSearchDispatch, onFileClicked, onProjectClicked, onShowPreview]);

    const displayLng = contentLanguage ?? currentLanguage;

    const dispatchActions = React.useMemo(() => actions, [actions]);
    const clearInput = React.useMemo(() => () => {
        updateState("", { allowFuzzy: true }, {}, undefined, undefined, undefined, undefined);
        dispatch(dispatchActions.setSearchResults(undefined));
    }, [dispatch, dispatchActions, updateState]);

    const getFilename = React.useMemo(() => (file: FileEntryDownloadDto, project: ProjectGroupDownloadDto): string => {
        // console.log("useCMSSearch.tsx::728 => location", location);
        // console.log("useCMSSearch.tsx::729 => displayLng", displayLng);
        // console.log("useCMSSearch.tsx::730 => file", file);

        const f = getCMSContent({
            filePosts: {},
            language: displayLng,
            exact: false,
            fileEntry: file,
            location: location
        });
        //file, project, displayLng, location, {});
        return f.media?.fileName ?? "";
    }, [location, displayLng]);



    React.useEffect(() => {
        dispatch(Actions.setCancel(clearInput));
        return () => {
            //console.log("useCMSSearch.tsx::178 => here??");

            dispatch(Actions.setCancel(undefined));
        }
    }, [clearInput, dispatch, dispatchActions]);


    const SearchResults = React.useMemo(() => {
        return (p: { isDisabled?: boolean, initpageSize?: number }) => {
            // const maxCount = maxCountHandling?.getMaxCount() ?? cmsConfig.maxSearchCount;
            const maxCount = maxCountHandling?.getMaxCount() ?? 12;
            const count = searchResults?.totalCount ?? 0;
            const backToken = searchResults?.backwardToken;
            const forwardToken = searchResults?.forwardToken;
            const curPage = Math.floor(Number.parseInt(routeData.token ?? "0") / maxCount);


            const paging: PagingHandler = {
                getCanNextPage: () => forwardToken !== undefined,
                getCanPrevPage: () => backToken !== undefined,
                getPageCount: () => Math.ceil(count / maxCount),
                getPageIndex: () => curPage,
                setPageIndex: async (updater) => {
                    const newPageIdx = typeof updater === "function" ? updater(curPage) : updater;
                    if (newPageIdx !== curPage) {
                        if (setLoadingPage)
                            setLoadingPage(true);
                        updateState(routeData.text, undefined, undefined, undefined, `${newPageIdx * maxCount}`, undefined, undefined);
                        if (setLoadingPage)
                            setLoadingPage(false);
                    }
                },
                setPageSize: maxCountHandling ? async (updater: Updater<number>) => {
                    const newMaxCount = typeof updater === "function" ? updater(maxCountHandling.getMaxCount()) : updater;
                    const newCurPage = Math.floor((curPage * maxCount) / newMaxCount);
                    maxCountHandling.setMaxCount(newMaxCount);
                    if (setLoadingPage)
                        setLoadingPage(true);
                    // if (searchCall)
                    //     await searchCall.call(undefined, `${newMaxCount * newCurPage}`, newMaxCount, true);
                    updateState(routeData.text, undefined, undefined, undefined, `${newMaxCount * newCurPage}`, undefined, undefined);
                    if (setLoadingPage)
                        setLoadingPage(false);
                } : undefined,
                getPageSize: maxCountHandling?.getMaxCount,
            };

            const data: useCMSSearchBasicSearchResultsProps<T, SearchData> =
            {
                getFilename, searchResults,
                t,
                filePosts, lng: displayLng, location,
                hidden, locations,
                isDisabled: p.isDisabled,
                columns, renderSubComponent,
                multiSelect, paging, onSelectionChanged: setSelection,
                changeSelectionHandler,
                initPageSize: p.initpageSize,
                searchResultTableWidthScaling,
            };
            return renderSearchResults ? renderSearchResults(data) : <BasicSearchResults {...data} />;

        }
    }, [getFilename, searchResults, t, multiSelect,
        setLoadingPage, setSelection, maxCountHandling, renderSearchResults,
        filePosts, displayLng, location, hidden, locations,
        updateState, changeSelectionHandler, columns, renderSubComponent,
        routeData.token, routeData.text, searchResultTableWidthScaling]);

    const getSuggestions = React.useMemo(() => async (text: string) => {
        if (text) {
            const res = await ControllerHelper.singleCall({ ...data, ...routeData, text } as any, getSuggestionsMethod, true);
            return res ?? [];
        }
        else
            return [];
    }, [data, routeData, getSuggestionsMethod]);

    const SF = React.useMemo(() => <SearchField
        term={routeData.text}
        clearInput={clearInput}
        getSuggestions={getSuggestions}
        placeholderText={placeholderText}
        updateState={updateState}
        ordered={routeData.ordered}
    />, [routeData.text, clearInput, getSuggestions, placeholderText, updateState, routeData.ordered]);

    return {
        SearchField: SF,
        SearchResults,
        searchResults,
        loading,
        selection,
        updateState,
    }
}

export default useCMSSearch;
